import type { ReactNode } from "react";
import { errorText } from "../api";

export function Loading({ label = "Loading..." }: { label?: string }) {
  return (
    <p className="state" role="status">
      {label}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="state muted">{children}</p>;
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="state error-box" role="alert">
      <span>{errorText(error)}</span>
      {onRetry && (
        <button type="button" className="btn small" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function PageHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <header className="page-header">
      <h2>{title}</h2>
      <div className="row">{children}</div>
    </header>
  );
}

const CHIP_TONE: Record<string, string> = {
  private: "muted",
  internal: "champ",
  public: "accent",
  draft: "muted",
  published: "gain",
  closed: "muted",
  long: "gain",
  short: "loss",
  succeeded: "gain",
  failed: "signal",
  running: "accent",
  queued: "muted",
};

export function Chip({ value }: { value: string }) {
  return <span className={`chip ${CHIP_TONE[value] ?? "muted"}`}>{value.replace(/_/g, " ")}</span>;
}

export const fmtNum = (n: number | null | undefined, dp = 2) =>
  n === null || n === undefined ? "n/a" : new Intl.NumberFormat("en-ZA", { maximumFractionDigits: dp }).format(n);

export const fmtCompact = (n: number | null | undefined) =>
  n === null || n === undefined ? "n/a" : new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(n);

/** Colour class for a signed number: gain above zero, loss below, none at zero or when missing. */
export const signClass = (n: number | null | undefined) => (n === null || n === undefined || !Number.isFinite(n) || n === 0 ? "" : n > 0 ? "pos" : "neg");
